import { Controller } from 'stimulus';
import Appsignal from 'utils/appsignal';

// The live Shopify Buy Button on a post or thread page (Block::Newsletter::ShopifyBuyButtonComponent#render_web).
// Loads Shopify's Buy Button SDK and builds the widget in this element from the values the server validated;
// the saved HTML can't carry a <script>, so this is the only place the SDK runs. `options` is the one,
// already-filtered options object for Shopify's createComponent; nothing is interpolated into code.
//
// The SDK loads once per page. Blocks for the same store share one UI so that they share a single cart and
// toggle, and the UI is dropped once its last block leaves the page (a Turbo visit replaces the body, and
// the cart lives in it).

const SDK_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

let sdkPromise = null;
const stores = new Map();

function loadSdk() {
  if (window.ShopifyBuy?.UI) return Promise.resolve(window.ShopifyBuy);

  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = SDK_URL;
      script.onload = () => resolve(window.ShopifyBuy);
      script.onerror = () => {
        sdkPromise = null;
        script.remove();
        reject(new Error('Shopify Buy Button SDK failed to load'));
      };
      (document.head || document.body).appendChild(script);
    });
  }
  return sdkPromise;
}

function acquireUi(domain, token) {
  const key = `${domain}:${token}`;
  let store = stores.get(key);
  if (!store) {
    const ui = loadSdk().then(ShopifyBuy => {
      const client = ShopifyBuy.buildClient({ domain, storefrontAccessToken: token });
      return ShopifyBuy.UI.onReady(client);
    });
    store = { ui, users: 0 };
    stores.set(key, store);
  }
  store.users += 1;
  return { key, ui: store.ui };
}

function releaseUi(key) {
  const store = stores.get(key);
  if (!store) return;
  store.users -= 1;
  if (store.users <= 0) stores.delete(key);
}

export default class extends Controller {
  static values = {
    type: String,
    domain: String,
    token: String,
    resourceId: String,
    moneyFormat: String,
    options: Object,
  };

  connect() {
    // A page restored from the Turbo cache brings its old widget along; start from an empty node.
    this.element.replaceChildren();

    const { key, ui } = acquireUi(this.domainValue, this.tokenValue);
    this.uiKey = key;
    // Tells this connect's callback from an earlier one: a reconnect can land on the same store key.
    const connection = this.connection = {};

    ui.then(shopifyUi => {
      if (this.connection !== connection || !this.element.isConnected) return;

      shopifyUi.createComponent(this.typeValue, {
        id: this.resourceIdValue,
        node: this.element,
        moneyFormat: encodeURIComponent(this.moneyFormatValue),
        options: this.optionsValue,
      });
    }).catch(error => {
      Appsignal.sendError(error);
    });
  }

  disconnect() {
    releaseUi(this.uiKey);
    this.uiKey = null;
    this.connection = null;
  }
}
